import React, { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';

import { IContext } from './types/context';
import { TErrors } from './types/errors';
import { TRoutePoint } from './types/routePoints';

import useAddRoutePoint from './hooks/useAddRoutePoint';
import useDragEnd from './hooks/useDragEnd';
import useChangeRoutePoint from './hooks/useChangeRoutePoint';
import useDelRoutePoint from './hooks/useDelRoutePoint';
import useResetRoutePoints from './hooks/useResetRoutePoints';
import useDelRoutePointError from './hooks/useDelRoutePointError';

import { defaultRoutePoints } from './utils';

export const RoutePointsContext = createContext({} as IContext);
export const useRoutePointsContext = () => useContext(RoutePointsContext);

const RoutePointsProvider = (props: PropsWithChildren) => {
  const [routePointErrors, setRoutePointErrors] = useState<TErrors>([]);
  const [routePoints, setRoutePoints] = useState<TRoutePoint[]>(
    () => defaultRoutePoints,
  );

  const onAddRoutePoint = useAddRoutePoint(setRoutePoints);
  const onDragEnd = useDragEnd(setRoutePoints);
  const onChangeRoutePoint = useChangeRoutePoint(setRoutePoints);
  const onDelRoutePoint = useDelRoutePoint(setRoutePoints);
  const onResetRoutePoints = useResetRoutePoints(setRoutePoints);

  const onDelRoutePointError = useDelRoutePointError(setRoutePointErrors);

  const value = useMemo(
    () => ({
      routePointErrors,
      setRoutePointErrors,
      routePoints,
      onAddRoutePoint,
      onChangeRoutePoint,
      onDelRoutePoint,
      onDragEnd,
      onResetRoutePoints,
      onDelRoutePointError,
      setRoutePoints,
    }),
    [
      routePointErrors,
      setRoutePointErrors,
      routePoints,
      onAddRoutePoint,
      onChangeRoutePoint,
      onDelRoutePoint,
      onDragEnd,
      onResetRoutePoints,
      onDelRoutePointError,
      setRoutePoints,
    ],
  );

  return (
    <RoutePointsContext.Provider value={value}>
      {props.children}
    </RoutePointsContext.Provider>
  );
};

export default RoutePointsProvider;
